import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarX2, CheckCircle2, MessageCircle } from 'lucide-react';
import { getMissedLastClass, MissedLastClass } from '../members/api';
import PersonAvatar from '../../components/PersonAvatar';
import { getWhatsAppLink } from '../../utils/whatsapp';
import { Skeleton } from '../../components/Skeleton';
import { readCache, writeCache } from '../../services/localCache';

/** Texto "hace X" legible a partir de días. */
function agoLabel(days: number): string {
  if (days <= 0) return 'hoy';
  if (days === 1) return 'ayer';
  if (days < 7) return `hace ${days} días`;
  if (days < 14) return 'hace 1 sem';
  return `hace ${Math.floor(days / 7)} sem`;
}

export default function MissedLastClassPanel() {
  // Pinta al instante lo último conocido (caché local) y refresca por detrás.
  const [rows, setRows] = useState<MissedLastClass[] | null>(() => readCache<MissedLastClass[]>('missed-last') ?? null);
  // Alumnas activas = denominador del porcentaje (Mabel 28-oct-2026, #1592): «31» no dice nada sin saber de cuántas.
  const [activas, setActivas] = useState<number | null>(() => readCache<number>('missed-last-activas') ?? null);

  useEffect(() => {
    getMissedLastClass(30).then((r) => {
      setRows(r.data);
      writeCache('missed-last', r.data);
      if (typeof r.summary?.active_students === 'number') { setActivas(r.summary.active_students); writeCache('missed-last-activas', r.summary.active_students); }
    }).catch(() => setRows((prev) => prev ?? []));
  }, []);
  const pct = rows && rows.length > 0 && activas ? Math.round((rows.length / activas) * 100) : null;

  return (
    <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-content">
          <CalendarX2 size={16} className="text-accent-ink" /> Faltaron a su última clase
        </h3>
        {rows && rows.length > 0 && (
          <span className="shrink-0 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-600" data-testid="faltaron-badge"
            title={pct != null ? `${rows.length} de ${activas} alumnas activas` : undefined}>
            {rows.length}{pct != null && <span className="ml-1 font-normal opacity-75">({pct} %)</span>}
          </span>
        )}
      </div>

      {rows === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-1 py-6 text-center">
          <CheckCircle2 size={26} className="text-emerald-500" />
          <p className="text-sm font-medium text-content">Sin ausencias recientes</p>
          <p className="text-xs text-content-3">Nadie faltó a su última clase (últimos 30 días).</p>
        </div>
      ) : (
        <ul className="-mr-1 max-h-72 space-y-1 overflow-y-auto pb-3 pr-1 [scrollbar-width:thin]">
          {rows.map((s) => {
            const name = `${s.firstname} ${s.lastname}`.trim();
            const msg = `Hola ${s.firstname}, te extrañamos en ${s.class_name} 💛 ¿Todo bien? Aquí te esperamos en Let's Move.`;
            const wa = s.phone_number ? getWhatsAppLink(s.phone_prefix, s.phone_number, msg) : null;
            return (
              <li key={s.account_id} className="flex items-center gap-2.5 rounded-lg px-1 py-1 transition hover:bg-surface-2">
                <Link to={`/alumnos/${s.account_id}`} className="flex min-w-0 flex-1 items-center gap-2.5">
                  <PersonAvatar contactId={s.account_id} hasPhoto={s.has_photo}
                    name={s.firstname} lastName={s.lastname} size="h-8 w-8" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-content">{name}</span>
                    <span className="flex items-center gap-1 truncate text-xs text-content-3">
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: s.color || '#94a3b8' }} />
                      {s.class_name} · {agoLabel(s.days_ago)}
                    </span>
                  </span>
                </Link>
                {wa && (
                  <a href={wa} target="_blank" rel="noreferrer" title="Escribir por WhatsApp"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 transition hover:bg-emerald-500/25">
                    <MessageCircle size={15} />
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
